import { bindActionCreators } from 'redux';
import cloneDeep from 'lodash/cloneDeep';

import { getSelectionOrder } from '~scenes/inplay/services';
import { isGenericSportV2 } from '~spa/Pages/SportGenericV2/utils';

import * as commonDataListEventsActions from '../actions/commonDataListEventsActions';

/**
 * React-redux bindings
 * @param {*} state the current state
 * @returns {object} props
 */
export function mapStateToProps(state) {
    return {
        events: state.commonDataListEvents.events,
        commonDataEvents: state.commonData,
        metaData: state.metaData,
    };
}

/**
 * React-redux bindings
 * @param {*} dispatch the dispatcher function
 * @returns {object} betfeed actions
 */
export function mapDispatchToProps(dispatch) {
    return bindActionCreators(commonDataListEventsActions, dispatch);
}

/**
 * Filter selection pointers when an event transitions from preplay to inplay.
 * Removes stale ones, adds new ones for the new inplay event.
 * @param {Object} param0 :-
 *  - {Boolean} hasThreeMarketView e.g. basketball, baseball, american football
 *  - {Array} stateSelectionPointers all existing selection pointers
 *  - {Number} ppEventId the PP event id
 *  - {Number} ipEventId the IP event id
 *  - {Boolean} ppIpTransitionSupported do we need selection pointers for the new event?
 * @returns {Array} the new array of selection pointers
 */
function getNewSelectionPointers({
    usesThreeMarketView, stateSelectionPointers, ppEventId, ipEventId, ppIpTransitionSupported,
}) {
    /**
     * @todo If we are guaranteed that we get selections with the websocket message
     * we loop over the new selections (in wsExtraEventsInfo) and can register at
     * selection level. However https://dedsert.atlassian.net/browse/BT-10062 means
     * we might not get selections if there is no active market when the event goes
     * inplay. For now we subscribe at market level
     * Only GENERIC_LAYOUT_SPORTS supported
     */
    const newSelectionPointers = [];
    // we can have marketId on PP-IP transition so we use the PP event's market for the new
    // websocket registrations.
    if (usesThreeMarketView) {
        stateSelectionPointers.forEach(({ event_id, market_id }) => {
            if (event_id !== ppEventId) {
                // other events
                newSelectionPointers.push({ event_id, market_id });
            } else if (ppIpTransitionSupported) {
                // the new IP event
                newSelectionPointers.push({ event_id: ipEventId, market_id });
            }
        });

        return newSelectionPointers;
    }

    // single market view
    let ipMarketId;
    stateSelectionPointers.forEach(({ event_id, market_id }) => {
        if (event_id !== ppEventId) {
            // other events
            newSelectionPointers.push({ event_id, market_id });
        }
        if (!ipMarketId) {
            ipMarketId = market_id;
        }
    });
    // the new event - outside the above loop because the PP event might have selection level
    // registrations and we only want one market level registration
    if (ppIpTransitionSupported && ipMarketId) {
        newSelectionPointers.push({ event_id: ipEventId, market_id: ipMarketId });
    }

    return newSelectionPointers;
}

/**
 * Format an array of selections (from inplay_start message) into a payload for
 * /inplay-req/market_selections_ordering" endpoint
 * @param {Array} wsDataSelections the selections
 * @returns {Object} the payload
 */
function preparGetSelectionOrderPayload(wsDataSelections) {
    const selectionOrderingPayload = {
        selections: {},
        selectionSchemas: {},
    };
    wsDataSelections.forEach((selection) => {
        const { selection_schema, market_id, id } = selection;
        if (!selectionOrderingPayload.selectionSchemas[market_id]) {
            selectionOrderingPayload.selectionSchemas[market_id] = { selection_schema };
            selectionOrderingPayload.selections[market_id] = {};
        }
        selectionOrderingPayload.selections[market_id][id] = selection;
    });

    return selectionOrderingPayload;
}

/**
 * Add new inplay events to the state (from inplay_start websocket message). Returns the new state and data
 * required for websocket (un)subscriptions
 * @param {Object} state redux state
 * @param {Object} wsData websocket message
 * @returns {Object} { (Object)newState, (Set)unsubscribeEventIds, (Array)newSelectionPointers }
 */
export function addNewInplayEventsFromWs(state, wsData) {
    const {
        events: wsPpEventIds,
        inplay_events: wsPpToIpIds,
    } = wsData;

    const newState = cloneDeep(state);
    const { events: stateEvents, selectionPointers: stateSelectionPointers } = newState;
    const unsubscribeEventIds = new Set();
    let newSelectionPointers = [];

    wsPpEventIds.forEach((wsPpEventId) => {
        // find event in state
        stateEvents.some((statePpEvent, index) => {
            const { event_id: statePpId, usesThreeMarketView } = statePpEvent;

            if (statePpId === wsPpEventId) {
                const ipId = wsPpToIpIds[statePpId];
                const newIpEvent = {
                    event_id: ipId,
                    addedFromWs: true,
                };
                unsubscribeEventIds.add(statePpId);

                // replace PP event with IP
                newState.events[index] = {
                    ...newState.events[index],
                    ...newIpEvent,
                };

                newSelectionPointers = getNewSelectionPointers({
                    hasThreeMarketView: usesThreeMarketView,
                    stateSelectionPointers,
                    ppEventId: statePpId,
                    ipEventId: ipId,
                    ppIpTransitionSupported: isGenericSportV2(statePpEvent.sportSlug),
                });

                newState.selectionPointers = newSelectionPointers;
                newState.events = stateEvents;

                return true;
            }

            return false;
        });
    });

    return { newState, unsubscribeEventIds, newSelectionPointers };
}

/**
 * Call the selection ordering endpoint for the selections in the new inplay event.
 * Sports using the 3 market view are already ordered
 * @param {Object} wsData the websocket data - intentionally mutated
 * @returns {Object} updated websocket data
 */
export async function getSelectionOrderingForInplayStart(wsData) {
    const getMarketSelectionsOrderingPromises = {
        eventIds: [],
        promises: [],
    };

    Object.keys(wsData.extra_events_info).forEach((ipEventId) => {
        const { selections = false, grouped_market_ids = [] } = wsData.extra_events_info[ipEventId];
        const usesThreeMarketView = grouped_market_ids.length > 1;
        if (selections !== false && !usesThreeMarketView) {
            const selectionOrderingPayload = preparGetSelectionOrderPayload(selections);
            getMarketSelectionsOrderingPromises.eventIds.push(ipEventId);
            getMarketSelectionsOrderingPromises.promises.push(getSelectionOrder(selectionOrderingPayload));
        }
    });

    if (!getMarketSelectionsOrderingPromises.eventIds.length) {

        return wsData;
    }

    const selectionOrder = await Promise.all(getMarketSelectionsOrderingPromises.promises);
    selectionOrder.forEach((resolved, index) => {
        const eventId = getMarketSelectionsOrderingPromises.eventIds[index];
        Object.keys(resolved).forEach((marketId) => {
            const orderedSelections = [];
            const selectionIds = resolved[marketId];
            selectionIds.forEach((selectionId, index) => {
                const wsSelections = wsData.extra_events_info[eventId].selections;
                const newSelection = wsSelections.find(({ id }) => id === selectionId);
                if (newSelection) {
                    orderedSelections.push({ ...newSelection, order: index, type: newSelection.selection_type });
                }
            });
            wsData.extra_events_info[eventId].selections = orderedSelections;
        });
    });

    return wsData;
}
